import { Box, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DrasticEppnaForm as DrasticEppnaFormType } from "../../../types/forms/capture";
import { DrasticEppnaResult } from "../../../types/interfaces/methods";
import DrasticEppnaForm from "./DrasticEppnaForm";

interface CaptureDrasticEppnaProps {
  canEdit: boolean;
  onCreate: (values: DrasticEppnaFormType) => Promise<void>;
  drasticEppnaInputs?: DrasticEppnaFormType | null;
  drasticEppnaResult?: DrasticEppnaResult | null;
}

export default function CaptureDrasticEppna({
  canEdit,
  onCreate,
  drasticEppnaInputs,
  drasticEppnaResult
}: Readonly<CaptureDrasticEppnaProps>) {
  const { t } = useTranslation();

  return (
    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
      <Stack spacing={2}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ minWidth: 320 }}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: 2,
                marginBottom: 4,
              }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 1.5 }}>
                {t("calculations.vulnerability.drastic-eppna.info.title")}
              </Typography>

              <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                {t("calculations.vulnerability.drastic-eppna.info.text1")}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1.5, fontStyle: "italic" }}>
                {t("calculations.vulnerability.drastic-eppna.info.text2")}
              </Typography>

              <Box component="ul" sx={{ pl: 3, mt: 0, mb: 0, color: "text.primary", lineHeight: 1.7 }}>
                {["D","R","A","S","T","I","C"].map(param => (
                  <li key={param}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t(`calculations.vulnerability.drastic-eppna.info.${param}`)}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Paper>
            <DrasticEppnaForm
              onSubmit={async (values) => {
                await onCreate(values);
              }}
              drasticEppnaInputs={drasticEppnaInputs}
              canEdit={canEdit}
            />
          </Box>
        </Box>

        {drasticEppnaResult && (
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                {t("calculations.vulnerability.drastic-eppna.result.title")}
              </Typography>

              <Box
                sx={{
                  border: "2px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                    borderRight: "1px solid",
                    borderRightColor: "divider",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(
                      "calculations.vulnerability.drastic-eppna.result.general-pollution"
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {drasticEppnaResult?.results.drasticIndex.toFixed(2)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(
                      "calculations.vulnerability.drastic-eppna.result.pesticide-use-pollution"
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {drasticEppnaResult?.results.drasticPesticideIndex.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}
      </Stack>
    </Paper>
  );
}
